import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Factory, Sprout, Heart, ArrowRight, CheckCircle, Play, AlertTriangle, Clock, TrendingUp, BarChart3, Users, Eye, Target } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import BentoCard from '../components/BentoCard';
import GlassCard from '../components/GlassCard';
import mediaAssets from '../config/mediaAssets';

const Home = ({ onRequestDemo }) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isProductVideoOpen, setIsProductVideoOpen] = useState(false);
  
  const solutions = [
    {
      icon: Shield,
      title: 'Security & Surveillance',
      description: 'Stop watching screens. Start seeing events.',
      path: '/solutions/security',
      image: mediaAssets.solutionImages.security.hero,
      video: mediaAssets.videos.security
    },
    {
      icon: Factory,
      title: 'Industry 4.0',
      description: 'Make every shift more consistent than the last.',
      path: '/solutions/industry-4',
      image: mediaAssets.solutionImages.industry.hero,
      video: mediaAssets.videos.industry
    },
    {
      icon: Sprout,
      title: 'Agriculture Intelligence',
      description: 'Modern farms run on visibility.',
      path: '/solutions/agriculture',
      image: mediaAssets.solutionImages.agriculture.hero,
      video: mediaAssets.videos.agriculture
    },
    {
      icon: Heart,
      title: 'Healthcare (WIP)',
      description: 'In development. Built responsibly.',
      path: '/solutions/healthcare',
      badge: 'Coming Soon',
      image: mediaAssets.solutionImages.healthcare.hero
    }
  ];

  const useCases = [
    { icon: AlertTriangle, text: 'Perimeter intrusion and restricted-zone alerts' },
    { icon: Eye, text: 'Loitering and anomaly detection' },
    { icon: Users, text: 'People/vehicle counting and movement patterns' },
    { icon: TrendingUp, text: 'Heatmaps and situational awareness' },
    { icon: Target, text: 'Incident search and investigation' },
    { icon: CheckCircle, text: 'SOP execution and audit-ready reporting' }
  ];

  const deliverySteps = [
    { name: 'Discover', description: 'Site walkthroughs and requirement mapping', num: '01' },
    { name: 'Design', description: 'Solution architecture and workflow design', num: '02' },
    { name: 'Deploy', description: 'System integration and configuration', num: '03' },
    { name: 'Integrate', description: 'Connect with existing systems', num: '04' },
    { name: 'Operate & Improve', description: 'Ongoing tuning and optimisation', num: '05' }
  ];

  return (
    <div className="min-h-screen">
      {/* Cinematic Hero - Split Layout */}
      <section className="min-h-screen pt-20 pb-12 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#EAF6E6] via-white to-[#F7FAF7]" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
            {/* Left: Copy + CTAs */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1 className="heading-1 mb-6">
                Know what's happening.<br />
                The moment it matters.
              </h1>
              <p className="body-large text-[var(--text-secondary)] mb-8 max-w-xl">
                Ferntree combines a modular analytics platform with expert deployment and ongoing optimisation—so your teams get outcomes, not dashboards.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button onClick={onRequestDemo} className="btn-primary">
                  Request a Demo
                </button>
                <Link to="/contact" className="btn-secondary">
                  Talk to an Expert
                </Link>
              </div>

              <button
                onClick={() => setIsVideoOpen(true)}
                className="inline-flex items-center gap-2 text-[var(--accent-text)] font-medium hover:gap-3 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--accent-primary)] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                </div>
                Watch 45s overview
              </button>

              {/* Trust strip */}
              <div className="mt-12 flex flex-wrap gap-6 text-sm text-[var(--text-muted)]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--accent-primary)]" />
                  <span>Based in Sydney, Australia</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--accent-primary)]" />
                  <span>Secure-by-design</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--accent-primary)]" />
                  <span>Deployment options to fit your environment</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Hero Video + Floating UI Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Hero video with fallback image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster={mediaAssets.heroImages.main}
                  className="w-full h-[600px] object-cover"
                >
                  <source src={mediaAssets.videos.hero} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* Floating glass cards - overlay */}
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <GlassCard
                    icon={AlertTriangle}
                    title="Perimeter Alert"
                    subtitle="Zone 3"
                    delay={0.3}
                  />
                  <GlassCard
                    icon={Clock}
                    title="Incident Timeline"
                    subtitle="Reviewed / Open"
                    delay={0.4}
                  />
                  <GlassCard
                    icon={TrendingUp}
                    title="Occupancy Trend"
                    subtitle="24 hours"
                    delay={0.5}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <GlassCard
                    icon={CheckCircle}
                    title="Checklist Status"
                    subtitle="In Progress"
                    delay={0.6}
                  />
                  <GlassCard
                    icon={BarChart3}
                    title="Anomaly Detected"
                    subtitle="Needs Review"
                    delay={0.7}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip - Full Width */}
      <section className="py-6 bg-white/80 backdrop-blur-sm border-y border-[var(--border-light)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-[var(--text-secondary)]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--accent-primary)]" />
              <span>Based in Sydney, Australia</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--accent-primary)]" />
              <span>Secure-by-design, privacy-aware delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--accent-primary)]" />
              <span>Deployable edge / on-prem / cloud-ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Ferntree Model - Bento Grid */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-[#F7FAF7]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 mb-4">The Ferntree model</h2>
            <p className="body-large text-[var(--text-secondary)]">
              A platform built to perform. A team built to deliver.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <BentoCard delay={0.1}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Platform core</h3>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Analytics engine: rules, detections, thresholds</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Dashboards: multi-site visibility and reporting</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Alerts: route, escalate, acknowledge, audit</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Evidence trail: consistent incident history</span>
                </li>
              </ul>
            </BentoCard>

            <BentoCard delay={0.2}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Services-led delivery</h3>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Discovery and site mapping</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Deployment and integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Training and handover</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-body)]">Managed support and tuning</span>
                </li>
              </ul>
            </BentoCard>
          </div>

          {/* Delivery flow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-3 text-sm"
          >
            <span className="font-semibold text-[var(--text-secondary)]">Delivery flow:</span>
            <span className="px-4 py-2 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full font-medium">Discover</span>
            <ArrowRight className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="px-4 py-2 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full font-medium">Design</span>
            <ArrowRight className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="px-4 py-2 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full font-medium">Deploy</span>
            <ArrowRight className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="px-4 py-2 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full font-medium">Integrate</span>
            <ArrowRight className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="px-4 py-2 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full font-medium">Operate & Improve</span>
          </motion.div>
        </div>
      </section>

      {/* What Ferntree delivers - Bento Grid with Images */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 mb-4">What Ferntree delivers</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <Link key={index} to={solution.path}>
                <BentoCard delay={index * 0.1} hoverable={true}>
                  <div className="relative h-48 -m-6 mb-6 rounded-t-2xl overflow-hidden group">
                    {solution.video ? (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        poster={solution.image}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      >
                        <source src={solution.video} type="video/mp4" />
                      </video>
                    ) : (
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <solution.icon className="w-10 h-10 text-white" />
                    </div>
                    {solution.badge && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-[var(--accent-text)] text-xs font-medium rounded-full">
                          {solution.badge}
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="heading-3 mb-2">{solution.title}</h3>
                  <p className="text-[var(--text-secondary)] mb-4">{solution.description}</p>
                  <span className="inline-flex items-center gap-2 text-[var(--accent-primary)] font-medium group-hover:gap-3 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </BentoCard>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product in Action - Video Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#F7FAF7] to-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-2 mb-4">See Ferntree in action</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden shadow-2xl mb-12 group cursor-pointer"
            onClick={() => setIsProductVideoOpen(true)}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={mediaAssets.heroImages.main}
              className="w-full h-[400px] object-cover"
            >
              <source src={mediaAssets.videos.productDemo} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[var(--accent-primary)] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-white fill-white ml-1" />
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              'Faster response',
              'Less manual monitoring',
              'Clearer audit trail'
            ].map((outcome, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <CheckCircle className="w-8 h-8 text-[var(--accent-primary)] mx-auto mb-3" />
                <p className="font-semibold text-[var(--text-primary)]">{outcome}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases - Icon + Line */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 mb-4">Use cases</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-4 p-4 bg-white border border-[var(--border-light)] rounded-xl hover:border-[var(--accent-primary)] hover:shadow-lg transition-all"
              >
                <useCase.icon className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-body)]">{useCase.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Deliver - Interactive Stepper */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-[#EAF6E6]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 mb-4">How we deliver</h2>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6">
            {deliverySteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-white border border-[var(--border-light)] rounded-2xl p-6 hover:border-[var(--accent-primary)] hover:shadow-lg transition-all">
                  <div className="text-4xl font-bold text-[var(--accent-primary)] mb-4 opacity-40">
                    {step.num}
                  </div>
                  <h4 className="font-semibold text-[var(--text-primary)] mb-2">{step.name}</h4>
                  <p className="text-sm text-[var(--text-secondary)]">{step.description}</p>
                </div>
                {index < deliverySteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-[var(--accent-primary)]" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ferntree */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 mb-4">Why Ferntree</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Built for real environments—not perfect lab conditions',
              'Integrates with what you already run',
              'Alerts that drive action, not noise',
              'Repeatable standards across multiple sites',
              'Improves over time through tuning and support'
            ].map((item, index) => (
              <BentoCard key={index} delay={index * 0.1}>
                <CheckCircle className="w-8 h-8 text-[var(--accent-primary)] mb-4" />
                <p className="text-[var(--text-body)] font-medium">{item}</p>
              </BentoCard>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-[var(--accent-wash)] to-[#EAF6E6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-6">
              Tell us your environment. We'll recommend the right deployment model.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={onRequestDemo} className="btn-primary">
                Request a Demo
              </button>
              <Link to="/contact" className="btn-secondary">
                Talk to an Expert
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modals */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        title="Ferntree Platform Overview"
      />
      <VideoModal
        isOpen={isProductVideoOpen}
        onClose={() => setIsProductVideoOpen(false)}
        title="See Ferntree in Action"
      />
    </div>
  );
};

export default Home;
